import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  User as UserIcon,
  Settings,
  Bell,
  ScrollText,
  ClipboardCheck,
  AlertTriangle,
  Menu,
  AlarmClock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { SidebarNav } from "@/components/Sidebar";
import { TID } from "@/constants/testIds";
import { ROLE_LABEL, formatDateTime, STATUS_LABEL } from "@/lib/format";
import { api } from "@/lib/api";
import { getPerms } from "@/lib/permissions";
import TicketThread from "@/components/TicketThread";
import { WarningSignDialog, WARNING_TYPE_LABEL } from "@/components/WarningsPanel";

export default function TopBar({ title, subtitle, actions }) {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [myDay, setMyDay] = useState(null);
  const [punchReqs, setPunchReqs] = useState([]);
  const [myWarnings, setMyWarnings] = useState([]);
  const [signWarning, setSignWarning] = useState(null);
  const [openTicketId, setOpenTicketId] = useState(null);
  const [popOpen, setPopOpen] = useState(false);

  const perms = getPerms(user?.role);
  const isSupport = user?.role === "super_admin";
  // FASE 17 — bell shows tickets for admins; pending checklists for the team
  const bellTickets = isSupport || perms.bellTickets;
  const unreadField = isSupport ? "unread_by_support" : "unread_by_tenant";

  const loadBell = async () => {
    if (!user) return;
    try {
      if (bellTickets) {
        const path = isSupport ? "/admin/tickets" : "/tickets";
        const { data } = await api.get(path);
        setTickets(Array.isArray(data) ? data : []);
      } else if (user.company_id) {
        const { data } = await api.get("/dashboard/my-day");
        setMyDay(data);
      }
    } catch (err) {
      console.debug("bell fetch failed", err);
    }
    // FASE E — pending timeclock requests (ajustes + justificativas) for HR roles
    if (["company_admin", "manager", "supervisor"].includes(user.role) && user.company_id) {
      try {
        const { data } = await api.get("/timeclock/adjustment-requests", {
          params: { status: "pending" },
        });
        setPunchReqs(Array.isArray(data) ? data : []);
      } catch (err) {
        console.debug("punch requests fetch failed", err);
      }
    }
    // Advertências pendentes de ciência do próprio usuário
    if (user.company_id && !["super_admin", "timeclock_kiosk"].includes(user.role)) {
      try {
        const { data } = await api.get("/timeclock/warnings");
        setMyWarnings(
          (Array.isArray(data) ? data : []).filter(
            (w) => w.user_id === user.id && w.status === "pending_signature",
          ),
        );
      } catch (err) {
        console.debug("warnings fetch failed", err);
      }
    }
  };

  useEffect(() => {
    loadBell();
    const t = setInterval(loadBell, 60_000); // poll every 60s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const unreadTickets = tickets.filter((t) => (t[unreadField] || 0) > 0);
  const ticketUnread = unreadTickets.reduce((s, t) => s + (t[unreadField] || 0), 0);

  const pendingExecs = myDay?.pending_executions || [];
  const nonConf = myDay?.non_conformities || [];
  const unreadCount =
    (bellTickets ? ticketUnread : pendingExecs.length + nonConf.length) +
    punchReqs.length +
    myWarnings.length;

  const initials = (user?.name || user?.email || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <header
      data-testid={TID.topbar}
      className="sticky top-0 z-20 flex items-center gap-3 sm:gap-4 border-b border-stone-200 bg-stone-50/90 backdrop-blur px-4 sm:px-6 py-4"
    >
      {/* FASE 20 — mobile drawer menu */}
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="md:hidden shrink-0 bg-white"
            data-testid="mobile-menu-btn"
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-72 p-0 bg-stone-950 text-stone-100 border-stone-900 overflow-y-auto"
          data-testid="mobile-menu-drawer"
        >
          <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
          <SidebarNav />
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-stone-950 truncate">
          {title}
        </h1>
        {subtitle && (
          <p className="font-body text-sm text-stone-500 mt-1 truncate">{subtitle}</p>
        )}
      </div>

      <div className="flex items-center gap-3">
        {actions}

        {company && (
          <Badge variant="outline" className="bg-white border-stone-300 hidden lg:inline-flex">
            {company.trade_name || company.legal_name}
          </Badge>
        )}

        <Popover open={popOpen} onOpenChange={setPopOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="border-stone-300 bg-white relative"
              data-testid="notifications-button"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-stone-50"
                  data-testid="notifications-count"
                >
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
            <div className="px-4 py-3 border-b border-stone-200">
              <div className="font-display font-bold tracking-tight">Notificações</div>
              <div className="text-xs text-stone-500 font-body">
                {unreadCount > 0
                  ? `${unreadCount} atualização(ões) pendente(s)`
                  : "Sem novidades no momento"}
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-stone-100">
              {myWarnings.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => {
                    setPopOpen(false);
                    setSignWarning(w);
                  }}
                  className="w-full text-left px-4 py-2.5 hover:bg-rose-50/60 flex items-start gap-2.5"
                  data-testid={`notification-warning-${w.id}`}
                >
                  <div className="h-7 w-7 rounded-md bg-rose-100 text-rose-800 flex items-center justify-center shrink-0">
                    <AlertTriangle className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-display font-semibold truncate text-rose-800">
                      Advertência — {WARNING_TYPE_LABEL[w.warning_type] || w.warning_type}
                    </div>
                    <div className="text-[11px] text-stone-500 font-body truncate">
                      Período {String(w.month).split("-").reverse().join("/")} · toque para ler e
                      registrar ciência
                    </div>
                  </div>
                </button>
              ))}
              {punchReqs.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => {
                    setPopOpen(false);
                    navigate("/timeclock/admin");
                  }}
                  className="w-full text-left px-4 py-2.5 hover:bg-indigo-50/50 flex items-start gap-2.5"
                  data-testid={`notification-punchreq-${r.id}`}
                >
                  <div className="h-7 w-7 rounded-md bg-indigo-100 text-indigo-800 flex items-center justify-center shrink-0">
                    <AlarmClock className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-display font-semibold truncate">
                      {r.kind === "absence" ? "Justificativa de falta" : "Ajuste de ponto"} —{" "}
                      {r.user_name}
                    </div>
                    <div className="text-[11px] text-stone-500 font-body truncate">
                      {(r.local_date || "").split("-").reverse().join("/")} · {r.reason}
                    </div>
                  </div>
                </button>
              ))}
              {bellTickets ? (
                <>
                  {unreadTickets.length === 0 && punchReqs.length === 0 && myWarnings.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-stone-500 font-body">
                      Nenhum ticket com resposta nova
                    </div>
                  )}
                  {unreadTickets.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setPopOpen(false);
                        setOpenTicketId(t.id);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-start gap-2.5"
                      data-testid={`notification-ticket-${t.id}`}
                    >
                      <div className="h-7 w-7 rounded-md bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
                        <ScrollText className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-display font-semibold truncate">
                          {t.subject}
                        </div>
                        <div className="text-[11px] text-stone-500 font-body truncate">
                          {STATUS_LABEL[t.status] || t.status} ·{" "}
                          {formatDateTime(t.last_activity_at || t.updated_at || t.created_at)}
                        </div>
                      </div>
                      <Badge className="bg-rose-600 text-white text-[10px] border-none shrink-0">
                        {t[unreadField]}
                      </Badge>
                    </button>
                  ))}
                </>
              ) : (
                <>
                  {pendingExecs.length === 0 && nonConf.length === 0 && punchReqs.length === 0 &&
                    myWarnings.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-stone-500 font-body">
                      Nenhum checklist em aberto hoje
                    </div>
                  )}
                  {pendingExecs.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => {
                        setPopOpen(false);
                        navigate(`/executions/${e.id}`);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-start gap-2.5"
                      data-testid={`notification-exec-${e.id}`}
                    >
                      <div className="h-7 w-7 rounded-md bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                        <ClipboardCheck className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-display font-semibold truncate">
                          {e.template_name || "Checklist"}
                        </div>
                        <div className="text-[11px] text-stone-500 font-body truncate">
                          {e.status === "in_progress" ? "Em andamento" : "Pendente"} ·{" "}
                          {formatDateTime(e.created_at)}
                        </div>
                      </div>
                    </button>
                  ))}
                  {nonConf.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => {
                        setPopOpen(false);
                        if (o.source_id) navigate(`/executions/${o.source_id}`);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-amber-50/60 flex items-start gap-2.5"
                      data-testid={`notification-nonconf-${o.id}`}
                    >
                      <div className="h-7 w-7 rounded-md bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
                        <AlertTriangle className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-display font-semibold truncate">
                          {o.title}
                        </div>
                        <div className="text-[11px] text-stone-500 font-body truncate">
                          Não conformidade · {formatDateTime(o.created_at)}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="border-stone-300 bg-white gap-2 pl-2 pr-3 h-9"
              data-testid={TID.topbarUserMenu}
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={user?.picture || ""} />
                <AvatarFallback className="text-[10px] bg-emerald-700 text-white">
                  {initials || "CC"}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:flex flex-col items-start leading-tight max-w-[150px]">
                <span className="text-sm font-body truncate w-full text-left">{user?.name}</span>
                <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-mono">
                  {ROLE_LABEL[user?.role] || user?.role}
                </span>
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="font-display font-bold">{user?.name}</div>
              <div className="text-xs font-body text-stone-500">{user?.email}</div>
              <div className="mt-1">
                <Badge className="bg-emerald-700 text-white">{ROLE_LABEL[user?.role] || user?.role}</Badge>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
              <Settings className="h-4 w-4 mr-2" /> Configurações
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-profile">
              <UserIcon className="h-4 w-4 mr-2" /> Meu painel
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              data-testid={TID.logoutButton}
            >
              <LogOut className="h-4 w-4 mr-2" /> Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <TicketThread
        ticketId={openTicketId}
        open={!!openTicketId}
        onOpenChange={(o) => !o && setOpenTicketId(null)}
        onUpdated={loadBell}
        showAdminControls={isSupport}
        viewerRole={isSupport ? "support" : "tenant"}
      />
      <WarningSignDialog
        warning={signWarning}
        open={!!signWarning}
        onOpenChange={(o) => !o && setSignWarning(null)}
        onSigned={loadBell}
      />
    </header>
  );
}
